import { useCallback, useEffect, useRef, useState } from 'react';

export const BIZY_GAME_HOLD_DURATION_MS = 4000;

const HOLD_DURATION_MS = BIZY_GAME_HOLD_DURATION_MS;

export const useBizyGameEasterEgg = () => {
	const holdTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const holdAnimationFrameRef = useRef<number>();
	const holdStartedAtRef = useRef(0);
	const [isPromptOpen, setIsPromptOpen] = useState(false);
	const [isGameOpen, setIsGameOpen] = useState(false);
	const [holdProgress, setHoldProgress] = useState(0);
	const [isHolding, setIsHolding] = useState(false);

	const clearHold = useCallback(() => {
		if (holdTimeoutRef.current) {
			clearTimeout(holdTimeoutRef.current);
			holdTimeoutRef.current = undefined;
		}
		if (holdAnimationFrameRef.current) {
			cancelAnimationFrame(holdAnimationFrameRef.current);
			holdAnimationFrameRef.current = undefined;
		}
	}, []);

	const resetHold = useCallback(() => {
		clearHold();
		holdStartedAtRef.current = 0;
		setHoldProgress(0);
		setIsHolding(false);
	}, [clearHold]);

	useEffect(() => {
		return () => resetHold();
	}, [resetHold]);

	const startBizyHold = useCallback(() => {
		if (isPromptOpen || isGameOpen) return;

		clearHold();
		holdStartedAtRef.current = performance.now();
		setIsHolding(true);
		setHoldProgress(0);

		const updateProgress = () => {
			const elapsed = performance.now() - holdStartedAtRef.current;
			setHoldProgress(Math.min(1, elapsed / HOLD_DURATION_MS));
			holdAnimationFrameRef.current = requestAnimationFrame(updateProgress);
		};

		holdAnimationFrameRef.current = requestAnimationFrame(updateProgress);
		holdTimeoutRef.current = setTimeout(() => {
			clearHold();
			setHoldProgress(1);
			setIsHolding(false);
			setIsPromptOpen(true);
		}, HOLD_DURATION_MS);
	}, [clearHold, isGameOpen, isPromptOpen]);

	const cancelBizyHold = useCallback(() => {
		if (isPromptOpen) return;
		resetHold();
	}, [isPromptOpen, resetHold]);

	const dismissPrompt = useCallback(() => {
		resetHold();
		setIsPromptOpen(false);
	}, [resetHold]);

	const openGame = useCallback(() => {
		resetHold();
		setIsPromptOpen(false);
		setIsGameOpen(true);
	}, [resetHold]);

	const closeGame = useCallback(() => {
		setIsGameOpen(false);
	}, []);

	return {
		isPromptOpen,
		isGameOpen,
		holdProgress,
		isHolding,
		startBizyHold,
		cancelBizyHold,
		dismissPrompt,
		openGame,
		closeGame,
	};
};
